import { useEffect, useRef } from "react";

/** Intervalo máximo entre teclas de um leitor físico (USB/Bluetooth emulando teclado). */
const MAX_KEY_GAP_MS = 60;
const MIN_CODE_LENGTH = 2;

function isTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

/**
 * Detecta leituras de um leitor físico de pulseira enquanto a tela não tem o leitor montado
 * (ex.: modo de validação facial): sequência rápida de teclas terminada em Enter.
 * Digitação humana (teclas espaçadas) é ignorada.
 */
export function useKeyboardWedge(enabled: boolean, onCode: (code: string) => void) {
  const bufferRef = useRef("");
  const lastKeyRef = useRef(0);
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTextField(event.target) || event.ctrlKey || event.metaKey || event.altKey) return;
      const now = performance.now();
      if (now - lastKeyRef.current > MAX_KEY_GAP_MS) bufferRef.current = "";
      lastKeyRef.current = now;
      if (event.key === "Enter") {
        const code = bufferRef.current.trim();
        bufferRef.current = "";
        if (code.length >= MIN_CODE_LENGTH) {
          event.preventDefault();
          onCodeRef.current(code);
        }
        return;
      }
      if (event.key.length === 1) bufferRef.current += event.key;
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      bufferRef.current = "";
    };
  }, [enabled]);
}
